/**
 * ============================================================================
 * LIFE & WEALTH SOLUTIONS - LICENSE STATUS DASHBOARD
 * Phoenix Rescue Apps - "Your Agency to AI Today"
 * ============================================================================
 * 
 * PURPOSE: Display license status, usage metrics, and IP protection status
 * 
 * FEATURES:
 * - Real-time license status
 * - Usage metrics and limits
 * - Circuit breaker status
 * - Security alerts
 * - Legal document access
 * 
 * ============================================================================
 */

import React, { useState, useEffect } from 'react';
import { 
  Shield, 
  AlertTriangle, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Activity,
  FileText,
  Users,
  Database,
  Zap,
  Lock
} from 'lucide-react';

const LicenseStatusDashboard = () => {
  const [licenseStatus, setLicenseStatus] = useState(null);
  const [usageMetrics, setUsageMetrics] = useState(null);
  const [circuitBreakers, setCircuitBreakers] = useState([]);
  const [securityReport, setSecurityReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadDashboardData();
    
    // Refresh every 5 minutes
    const interval = setInterval(loadDashboardData, 300000);
    return () => clearInterval(interval);
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Load license status
      const statusResponse = await fetch('/api/ip-protection/license/status');
      const statusData = await statusResponse.json();
      setLicenseStatus(statusData.status);

      // Load usage metrics
      const metricsResponse = await fetch('/api/ip-protection/license/usage-metrics');
      const metricsData = await metricsResponse.json();
      setUsageMetrics(metricsData.metrics);

      // Load circuit breakers
      const breakersResponse = await fetch('/api/ip-protection/ai-monitor/circuit-breakers');
      const breakersData = await breakersResponse.json();
      setCircuitBreakers(breakersData.circuitBreakers || []);

      // Load security report
      const endTime = new Date().toISOString();
      const startTime = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const reportResponse = await fetch(
        `/api/ip-protection/ai-monitor/security-report?startTime=${startTime}&endTime=${endTime}`
      );
      const reportData = await reportResponse.json();
      setSecurityReport(reportData.report);

      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-6 h-6 text-green-500" />;
      case 'expiring_soon':
        return <AlertTriangle className="w-6 h-6 text-yellow-500" />;
      case 'expired':
      case 'invalid':
      case 'suspended':
        return <XCircle className="w-6 h-6 text-red-500" />;
      default:
        return <Clock className="w-6 h-6 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-50 border-green-200';
      case 'expiring_soon':
        return 'bg-yellow-50 border-yellow-200';
      case 'expired':
      case 'invalid':
      case 'suspended':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-red-900 mb-2 text-center">Error Loading Dashboard</h2>
          <p className="text-red-700 text-center">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Shield className="w-8 h-8 text-blue-600" />
              IP Protection & License Dashboard
            </h1>
            <p className="text-gray-600 mt-1">Phoenix Rescue Apps - "Your Agency to AI Today"</p>
          </div>
          <button
            onClick={loadDashboardData}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Activity className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* License Status Card */}
      <div className={`border-2 rounded-lg p-6 mb-6 ${getStatusColor(licenseStatus?.status)}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {getStatusIcon(licenseStatus?.status)}
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {licenseStatus?.status === 'active' ? 'License Active' : 
                 licenseStatus?.status === 'expiring_soon' ? 'License Expiring Soon' :
                 licenseStatus?.status === 'expired' ? 'License Expired' :
                 licenseStatus?.status === 'suspended' ? 'License Suspended' :
                 'License Status Unknown'}
              </h2>
              <p className="text-gray-700 mt-1">{licenseStatus?.message}</p>
            </div>
          </div>
          {licenseStatus?.status === 'active' && (
            <div className="text-right">
              <div className="text-sm text-gray-600">Licensed To</div>
              <div className="font-semibold text-gray-900">{licenseStatus?.licensedTo}</div>
              <div className="text-sm text-gray-600 mt-2">License Type</div>
              <div className="font-semibold text-gray-900">{licenseStatus?.licenseType}</div>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Usage Metrics Card */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Database className="w-5 h-5 text-blue-600" />
            Usage Metrics
          </h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-gray-600" />
                <span className="text-gray-700">Active Users</span>
              </div>
              <div className="font-semibold text-gray-900">
                {usageMetrics?.activeUsers || 0}
                {licenseStatus?.limits?.maxUsers && (
                  <span className="text-gray-500 text-sm ml-1">
                    / {licenseStatus.limits.maxUsers}
                  </span>
                )}
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-700">Total Policies</span>
              <span className="font-semibold text-gray-900">
                {usageMetrics?.totalPolicies || 0}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-700">AI Agent Calls (Today)</span>
              <span className="font-semibold text-gray-900">
                {usageMetrics?.aiAgentCalls || 0}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-700">API Calls (Today)</span>
              <span className="font-semibold text-gray-900">
                {usageMetrics?.apiCalls || 0}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-700">Storage Used</span>
              <span className="font-semibold text-gray-900">
                {formatBytes(usageMetrics?.storageUsed || 0)}
              </span>
            </div>
          </div>
        </div>

        {/* Security Status Card */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-600" />
            Security Status (24h)
          </h3>
          {securityReport ? (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-700">Total Actions Monitored</span>
                <span className="font-semibold text-gray-900">
                  {securityReport.totalActions}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-700">Actions Blocked</span>
                <span className={`font-semibold ${securityReport.blockedActions > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                  {securityReport.blockedActions}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-700">Anomalies Detected</span>
                <span className={`font-semibold ${securityReport.anomaliesDetected > 0 ? 'text-yellow-600' : 'text-gray-900'}`}>
                  {securityReport.anomaliesDetected}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-700">Cognitive Attacks</span>
                <span className={`font-semibold ${securityReport.cognitiveAttacks > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                  {securityReport.cognitiveAttacks}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-700">Circuit Breakers Activated</span>
                <span className={`font-semibold ${securityReport.circuitBreakerActivations > 0 ? 'text-orange-600' : 'text-gray-900'}`}>
                  {securityReport.circuitBreakerActivations}
                </span>
              </div>
            </div>
          ) : (
            <p className="text-gray-500">No security data available</p>
          )}
        </div>
      </div>

      {/* Circuit Breakers Alert */}
      {circuitBreakers.filter(cb => cb.status === 'active').length > 0 && (
        <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-6 mb-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-orange-600 flex-shrink-0 mt-1" />
            <div className="flex-1">
              <h3 className="text-xl font-bold text-orange-900 mb-2">
                Active Circuit Breakers
              </h3>
              <p className="text-orange-800 mb-4">
                The following AI agents have been temporarily suspended due to anomalous behavior:
              </p>
              <div className="space-y-3">
                {circuitBreakers
                  .filter(cb => cb.status === 'active')
                  .map((breaker, index) => (
                    <div key={index} className="bg-white rounded-lg p-4 border border-orange-200">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-semibold text-gray-900">{breaker.agentType}</div>
                          <div className="text-sm text-gray-600 mt-1">
                            Reason: {breaker.reason}
                          </div>
                          <div className="text-sm text-gray-600">
                            Activated: {new Date(breaker.activatedAt).toLocaleString()}
                          </div>
                        </div>
                        <button
                          onClick={() => resetCircuitBreaker(breaker.agentType)}
                          className="px-3 py-1 bg-orange-600 text-white text-sm rounded hover:bg-orange-700 transition-colors"
                        >
                          Reset
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AI Agent Status by Type */}
      {securityReport && Object.keys(securityReport.byAgent).length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Zap className="w-5 h-5 text-blue-600" />
            AI Agent Activity (24h)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(securityReport.byAgent).map(([agentType, stats]) => (
              <div key={agentType} className="border border-gray-200 rounded-lg p-4">
                <div className="font-semibold text-gray-900 mb-2">{agentType}</div>
                <div className="text-sm space-y-1">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Actions:</span>
                    <span className="font-medium">{stats.total}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Blocked:</span>
                    <span className={`font-medium ${stats.blocked > 0 ? 'text-red-600' : ''}`}>
                      {stats.blocked}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Anomalies:</span>
                    <span className={`font-medium ${stats.anomalies > 0 ? 'text-yellow-600' : ''}`}>
                      {stats.anomalies}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Legal Documents Card */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-blue-600" />
          Legal Documents & IP Protection
        </h3>
        <p className="text-gray-600 mb-4">
          Access your legal documents and intellectual property protection agreements.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          <DocumentButton 
            title="End User License Agreement (EULA)"
            type="eula"
          />
          <DocumentButton 
            title="Non-Disclosure Agreement (NDA)"
            type="nda"
          />
          <DocumentButton 
            title="IP Protection Agreement"
            type="ip-protection"
          />
          <DocumentButton 
            title="Service Level Agreement (SLA)"
            type="sla"
          />
          <DocumentButton 
            title="Data Processing Agreement (DPA)"
            type="dpa"
          />
          <DocumentButton 
            title="Security Policy"
            type="security-policy"
          />
          <DocumentButton 
            title="Acceptable Use Policy"
            type="acceptable-use"
          />
        </div>
      </div>

      {/* Footer */}
      <div className="mt-8 text-center text-gray-500 text-sm">
        <div className="flex items-center justify-center gap-2 mb-2">
          <Lock className="w-4 h-4" />
          <span>Protected by Phoenix Rescue IP Protection System</span>
        </div>
        <p>© {new Date().getFullYear()} Phoenix Rescue Apps. All Rights Reserved.</p>
        <p className="mt-1">"Your Agency to AI Today"</p>
      </div>
    </div>
  );
};

const DocumentButton = ({ title, type }) => {
  const handleDownload = async () => {
    try {
      const response = await fetch(`/api/ip-protection/legal/document/${type}`);
      const data = await response.json();
      
      // Create download
      const blob = new Blob([data.document], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}.txt`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      alert('Failed to download document: ' + error.message);
    }
  };

  return (
    <button
      onClick={handleDownload}
      className="flex items-center gap-2 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-left"
    >
      <FileText className="w-4 h-4 text-gray-600 flex-shrink-0" />
      <span className="text-sm text-gray-900">{title}</span>
    </button>
  );
};

const resetCircuitBreaker = async (agentType) => {
  try {
    await fetch('/api/ip-protection/ai-monitor/reset-circuit-breaker', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ agentType })
    });
    window.location.reload();
  } catch (error) {
    alert('Failed to reset circuit breaker: ' + error.message);
  }
};

const formatBytes = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

export default LicenseStatusDashboard;
